const capitalize = (string: string, firstOnly = false) => {
  if (!string) return '';
  return (
    string.charAt(0).toUpperCase() +
    (firstOnly ? string.slice(1).toLowerCase() : string.slice(1).toLowerCase())
  );
};

/**
 * Calculates the remaining available ticket types after booker ticket selection
 * @param {Array} units <description>
 * @param {String} selectedTicket <description>
 * @return {Array}
 */
export const calculateRemainingTickets = (
  units: any[],
  selectedTicket: string | null
) => {
  const additionalTicketTypes: Array<{
    unitTypeId: number;
    unitTypeName: string;
    nounSingular: string;
    nounPlural: string;
  }> = [];

  // Safety check: return empty array if units is undefined/null
  if (!units || !Array.isArray(units)) {
    return additionalTicketTypes;
  }

  // Should only account for units with at least one ticket
  const ticketedUnits = units.filter((unit) => unit.count > 0);

  // Add each ticket type to array
  additionalTicketTypes.push(
    ...ticketedUnits.flatMap((unit) =>
      Array.from({ length: unit.count }, () => ({
        unitTypeId: unit.id,
        unitTypeName: unit.noun_singular.toLowerCase(),
        nounSingular: unit.noun_singular || '',
        nounPlural: unit.noun_plural || '',
      }))
    )
  );

  if (selectedTicket == null) {
    return additionalTicketTypes;
  }

  // Remove selected ticket from array
  const index = additionalTicketTypes.findIndex(
    ({ unitTypeName }) => unitTypeName === selectedTicket.toLowerCase()
  );
  if (index > -1) {
    additionalTicketTypes.splice(index, 1);
  }
  return additionalTicketTypes;
};

/**
 * Formats the available ticket types for Select component
 * @param {Array} units <description>
 * @return {Array}
 */
export const formatTicketTypes = (units: any[]) => {
  // Safety check: return empty array if units is undefined/null
  if (!units || !Array.isArray(units)) {
    return [];
  }

  return units
    .filter((unit) => unit.count > 0 && unit.noun_singular)
    .map((unit) => {
      const capitalizedName = capitalize(unit.noun_singular);
      return {
        label: capitalizedName,
        value: capitalizedName,
      };
    });
};

/**
 * Format bookerGuestName for GuestInformation component
 */
export const formatBookerGuestName = ({
  firstName,
  lastName,
}: {
  firstName: string | null;
  lastName: string | null;
}) => {
  if (firstName != null && lastName != null) {
    return `${firstName} ${lastName}`;
  }
  return '';
};

/**
 * Formats additional guests for an affiliate booking
 * @param {Array} affiliateGuests <description>
 * @return {Array}
 */
export const formatAffiliateGuests = (affiliateGuests: any[]) => {
  let affiliateGuestArray: any[] = [];

  if (affiliateGuests?.length > 0) {
    affiliateGuestArray = affiliateGuests.map((guest, index) => {
      return {
        first_name: '',
        last_name: '',
        full_name: '',
        position: index - 1,
        unit_type_id: guest.unitTypeId,
      };
    });
    // Remove first guest to account for "booker"
    affiliateGuestArray.shift();
  }

  return affiliateGuestArray;
};

/**
 * Returns list of formatted affiliate guests for form submission
 * @param {Array} Units<description>
 * @return {Array}
 */
export const getFormattedAffiliateGuests = (units: any[]) => {
  const affiliateGuests = calculateRemainingTickets(units, null);
  return formatAffiliateGuests(affiliateGuests);
};
